// The client half of the sync protocol: tracks the server's monotonic
// state_version, applies cable events through the reducer, and falls back to a
// full HTTP refetch whenever the stream skips a version. Solid Cable is
// broadcast-only and at-most-once, so it is this refetch — not the event
// stream — that guarantees the client converges on server state.
//
// Split out of useDraftChannel so the gap/reconnect path can be asserted on
// directly: the hook needs a renderer and there is no DOM test environment
// here (same reason countdownFrom is split out of useCountdown in lib/clock).

import { draftReducer } from './draftReducer'
import type { DraftState, ServerEvent } from '../types/protocol'

export interface DraftSyncSnapshot {
  draft: DraftState | null
  loading: boolean
  error: string | null
}

export interface DraftSyncPorts {
  /** Full-state HTTP read — the authoritative one. */
  fetchState: () => Promise<DraftState>
  /** Fires whenever draft, loading or error changes. */
  onChange: (snapshot: DraftSyncSnapshot) => void
  /** The player pool is not part of draft state, so it reloads separately. */
  onRankingsImported?: () => void
  /** Server clock sample carried by every event envelope. */
  onServerTime?: (serverNowMs: number) => void
}

export interface DraftSync {
  /** Full refetch. Also the reconnect path — connected() calls this. */
  refetch: () => Promise<void>
  /** Feed one raw cable payload. */
  handleEvent: (data: unknown) => void
  /** Last version applied locally; 0 until the first fetch lands. */
  version: () => number
}

export function createDraftSync(ports: DraftSyncPorts): DraftSync {
  let version = 0
  let snapshot: DraftSyncSnapshot = { draft: null, loading: true, error: null }

  const emit = (next: Partial<DraftSyncSnapshot>): void => {
    snapshot = { ...snapshot, ...next }
    ports.onChange(snapshot)
  }

  const refetch = async (): Promise<void> => {
    try {
      const state = await ports.fetchState()
      version = state.state_version
      emit({ draft: state, error: null, loading: false })
    } catch {
      // The last known state stays on screen — a stale board beats a blank
      // one on a phone that dipped off the wifi.
      emit({ error: 'Could not load the draft', loading: false })
    }
  }

  const handleEvent = (data: unknown): void => {
    const event = data as ServerEvent
    if (!event || typeof event !== 'object' || !('state_version' in event)) return

    // Record server time for clock offset synchronization.
    if (typeof event.server_now_ms === 'number') {
      ports.onServerTime?.(event.server_now_ms)
    }

    // Gap detection: anything other than exactly local + 1 means an event was
    // dropped, replayed or delivered out of order. The reducer can't repair
    // any of those, so refetch and take the server's word for it.
    if (event.state_version !== version + 1) {
      void refetch()
      return
    }

    version = event.state_version

    if (snapshot.draft) {
      emit({ draft: draftReducer(snapshot.draft, event) })
    }

    if (event.type === 'rankings_imported') {
      ports.onRankingsImported?.()
    }
  }

  return { refetch, handleEvent, version: () => version }
}
